import { useState, useEffect } from 'react';
import { config } from '@/config';

export interface BrandLogo {
  src: string;
  name: string;
}

function fileNameToName(filename: string): string {
  return filename
    .replace(/\.[^.]+$/, '')
    .replace(/[-_]/g, ' ')
    .trim();
}

/**
 * Fetch logo list from PHP endpoint, fall back to mock names if unavailable.
 */
export function useBrandLogos(): {
  logos: BrandLogo[];
  loading: boolean;
  error: string | null;
} {
  const [logos, setLogos] = useState<BrandLogo[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    const listUrl = config.logos.listUrl;
    const baseUrl = config.logos.url;
    const normalizedBase = baseUrl.endsWith('/') ? baseUrl : `${baseUrl}/`;

    if (!listUrl) {
      setLogos([]);
      setLoading(false);
      return;
    }

    fetch(listUrl, { method: 'GET', cache: 'no-store' })
      .then((res) => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        return res.json();
      })
      .then((filenames: string[]) => {
        if (cancelled) return;
        if (!Array.isArray(filenames) || filenames.length === 0) {
          setLogos([]);
          setLoading(false);
          return;
        }

        const loaded: BrandLogo[] = filenames.map((filename) => ({
          src: `${normalizedBase}${filename}`,
          name: fileNameToName(filename),
        }));

        setLogos(loaded);
        setLoading(false);
      })
      .catch((err) => {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Failed to load logos');
          setLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, []);

  return { logos, loading, error };
}